import { create } from 'zustand'
import { api } from '@/lib/api'

interface RequestItem {
  id: string
  userId: string
  userName: string | null
  userEmail: string | null
  personImageUri: string | null
  personImageTag: string | null
  clothingImages: unknown
  style: string | null
  status: string
  generationTimeMs: number | null
  totalRequestTimeMs: number | null
  creditsUsed: number | null
  errorMessage: string | null
  createdAt: string | null
}

interface RequestDetail extends RequestItem {
  customPrompt: string | null
  generatedPrompt: string | null
  errorCode: string | null
  requestIp: string | null
  userAgent: string | null
  startedAt: string | null
  completedAt: string | null
}

interface RequestStats {
  totalRequests30d: number
  typeBreakdown: Record<string, number>
  statusBreakdown: Record<string, number>
  avgGenerationTimeMs: number
  creditsBreakdown: Record<string, number>
  requestsByDay: Array<{ date: string; tag: string | null; count: number }>
}

interface StorageObject {
  key: string
  size: number
  uploaded: string
  httpMetadata: Record<string, string> | null
  publicUrl: string
}

interface StorageStats {
  totalObjects: number
  totalSize: number
  folders: Array<{ name: string; fileCount: number; totalSize: number }>
}

interface CostBreakdown {
  count: number
  cost: number
}

interface CostData {
  periods: Record<
    string,
    {
      compose: CostBreakdown
      enhance: CostBreakdown
      fashionIdentify: CostBreakdown
      groqTags: CostBreakdown
      total: number
    }
  >
  costsByDay: Array<{ date: string; compose: number; enhance: number; total: number }>
  pricing: Record<string, number>
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

interface MonitorState {
  requests: RequestItem[]
  requestsPagination: Pagination
  isLoadingRequests: boolean

  requestDetail: RequestDetail | null
  isLoadingDetail: boolean

  stats: RequestStats | null
  isLoadingStats: boolean

  storageObjects: StorageObject[]
  storagePrefixes: string[]
  storageCursor: string | undefined
  storageTruncated: boolean
  isLoadingStorage: boolean
  currentPrefix: string

  storageStats: StorageStats | null
  isLoadingStorageStats: boolean

  costs: CostData | null
  isLoadingCosts: boolean

  typeFilter: string
  statusFilter: string
  dateFrom: string
  dateTo: string
  userSearch: string

  error: string | null

  setTypeFilter: (type: string) => void
  setStatusFilter: (status: string) => void
  setDateFrom: (date: string) => void
  setDateTo: (date: string) => void
  setUserSearch: (search: string) => void

  fetchRequests: (page?: number) => Promise<void>
  fetchRequestDetail: (id: string) => Promise<void>
  clearDetail: () => void
  fetchStats: () => Promise<void>
  fetchStorage: (prefix?: string, append?: boolean) => Promise<void>
  fetchStorageStats: () => Promise<void>
  fetchCosts: () => Promise<void>
}

export const useMonitorStore = create<MonitorState>((set, get) => ({
  requests: [],
  requestsPagination: { page: 1, limit: 25, total: 0, totalPages: 0 },
  isLoadingRequests: false,

  requestDetail: null,
  isLoadingDetail: false,

  stats: null,
  isLoadingStats: false,

  storageObjects: [],
  storagePrefixes: [],
  storageCursor: undefined,
  storageTruncated: false,
  isLoadingStorage: false,
  currentPrefix: '',

  storageStats: null,
  isLoadingStorageStats: false,

  costs: null,
  isLoadingCosts: false,

  typeFilter: '',
  statusFilter: '',
  dateFrom: '',
  dateTo: '',
  userSearch: '',

  error: null,

  setTypeFilter: (typeFilter) => {
    set({ typeFilter })
    get().fetchRequests(1)
  },

  setStatusFilter: (statusFilter) => {
    set({ statusFilter })
    get().fetchRequests(1)
  },

  setDateFrom: (dateFrom) => set({ dateFrom }),
  setDateTo: (dateTo) => set({ dateTo }),
  setUserSearch: (userSearch) => set({ userSearch }),

  fetchRequests: async (page = 1) => {
    set({ isLoadingRequests: true, error: null })
    const { typeFilter, statusFilter, dateFrom, dateTo, userSearch } = get()
    try {
      const res = await api.get<{
        data: { requests: RequestItem[]; pagination: Pagination }
      }>('/api/v1/admin/monitor/requests', {
        page,
        limit: 25,
        type: typeFilter || undefined,
        status: statusFilter || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        userId: userSearch || undefined,
      })
      set({
        requests: res.data.requests,
        requestsPagination: res.data.pagination,
        isLoadingRequests: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingRequests: false })
    }
  },

  fetchRequestDetail: async (id) => {
    set({ isLoadingDetail: true, error: null })
    try {
      const res = await api.get<{ data: RequestDetail }>(`/api/v1/admin/monitor/requests/${id}`)
      set({ requestDetail: res.data, isLoadingDetail: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingDetail: false })
    }
  },

  clearDetail: () => set({ requestDetail: null }),

  fetchStats: async () => {
    set({ isLoadingStats: true, error: null })
    try {
      const res = await api.get<{ data: RequestStats }>('/api/v1/admin/monitor/requests/stats')
      set({ stats: res.data, isLoadingStats: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingStats: false })
    }
  },

  fetchStorage: async (prefix = '', append = false) => {
    set({ isLoadingStorage: true, error: null, currentPrefix: prefix })
    try {
      const cursor = append ? get().storageCursor : undefined
      const res = await api.get<{
        data: {
          objects: StorageObject[]
          prefixes: string[]
          truncated: boolean
          cursor?: string
        }
      }>('/api/v1/admin/monitor/storage', {
        prefix: prefix || undefined,
        limit: 50,
        cursor: cursor || undefined,
        delimiter: '/',
      })
      set({
        storageObjects: append ? [...get().storageObjects, ...res.data.objects] : res.data.objects,
        storagePrefixes: res.data.prefixes,
        storageTruncated: res.data.truncated,
        storageCursor: res.data.cursor,
        isLoadingStorage: false,
      })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingStorage: false })
    }
  },

  fetchStorageStats: async () => {
    set({ isLoadingStorageStats: true, error: null })
    try {
      const res = await api.get<{ data: StorageStats }>('/api/v1/admin/monitor/storage/stats')
      set({ storageStats: res.data, isLoadingStorageStats: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingStorageStats: false })
    }
  },

  fetchCosts: async () => {
    set({ isLoadingCosts: true, error: null })
    try {
      const res = await api.get<{ data: CostData }>('/api/v1/admin/monitor/costs')
      set({ costs: res.data, isLoadingCosts: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoadingCosts: false })
    }
  },
}))
